import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { invoicesAdvancedOrderSearch } from '../../../api/invoicing';
import { Search } from './Search'
import { fToTitle } from '../../../utils/formatText';
import InvoiceSearchResult from './InvoiceSearchResult';

export default function InvoiceSearch() {

  const [searchParams, setSearchParams] = useSearchParams();
  const [base] = useState(() => searchParams.get('base'), [searchParams]);
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState({ searchValue: '', selectedSearchTypes: [], page: 1, pageSize: 20 });
  const [searchResults, setSearchResults] = useState({});
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
  });
  const [initialFilters, setInitialFilters] = useState({...filters});
  const [initialSearchData, setInitialSearchData] = useState({ ...searchData });

  const searchTypes = [
    {value: 'invoiceId', label: 'Order Number'},
    {value: 'vendor', label: 'Vendor'},  
    {value: 'facility', label: 'Facility'},
    {value: 'gls', label: 'GLs'},
    {value: 'vendorInvoiceId', label: 'Invoice Number'},
    {value: 'total', label: 'Invoice Total'}
  ];

  const filtersChanged = useMemo(() => JSON.stringify(filters) !== JSON.stringify(initialFilters), [initialFilters, filters]);
  const isSearchDirty = useMemo(() => searchData.searchValue !== initialSearchData.searchValue ||
    JSON.stringify(searchData.selectedSearchTypes) !== JSON.stringify(initialSearchData.selectedSearchTypes) ||
    filtersChanged, [searchData, initialSearchData, filtersChanged]);
  
  const getSearchTypeFromUrl = (type) => {
    if (searchParams.get(type)?.toLowerCase() === "true"){
      return searchTypes.find(searchType => searchType.value === type);
    }
    return null;
  };

  useEffect(() => {
    const urlSearchTypes = searchTypes.map(type => getSearchTypeFromUrl(type.value)).filter(t => t);
    const urlData = {
      selectedSearchTypes: urlSearchTypes,
      searchValue: searchParams.get('searchValue'),
      pageSize: searchData.pageSize
    };
    const page = parseInt(searchParams.get('page'), 10) || 1;
    setSearchData({...urlData, page});
    setInitialSearchData(urlData);
    handleSearch(urlData, page);
  }, []);

  const handleInvoicesSearch = async(data, pageNum) => {
    setLoading(true);
    const sixMonthsAgo = (() => { const d = new Date(); d.setMonth(d.getMonth() - 6); return d; })();
    const {searchValue, selectedSearchTypes} = data;
    const advancedSearchResults = await invoicesAdvancedOrderSearch({
      startDate: filters.startDate || sixMonthsAgo,
      endDate: filters.endDate || new Date(),
      pageNumber: pageNum,
      pageSize: searchData.pageSize,
      searchValue,
      ...selectedSearchTypes.reduce((acc, type) => {
        acc[type.value] = true;
        return acc;
      }, {})
    });
    setSearchResults(advancedSearchResults.data);
    setLoading(false);
  }

  const handleSearch = async (data, page) => {
    setSearchData({...data, page});
    if (!data.searchValue || !data.selectedSearchTypes?.length) return;
    const submitData = { searchValue: data.searchValue, page }
    data.selectedSearchTypes.forEach((type) => { submitData[type.value] = true; });
    setSearchParams({ ...submitData, base});
    setInitialSearchData({ ...data });
    setInitialFilters({...filters});
    handleInvoicesSearch(data, page);
  };

  const searchDataMap = useMemo(() => searchResults?.searchResults?.map((searchResult) => 
    <InvoiceSearchResult
      searchResult={searchResult}
      search={searchData.searchValue}
      key={searchResult.id}

    />
  ), [searchResults]);

  const handlePaginationChange = (page) => {
    setSearchData(data => ({...data, page}));
    handleInvoicesSearch(searchData, page);
  }

  const backToPath = `/dashboard/invoicing/${base.slice(0, base.indexOf('Invoicing'))}`;

  return <Search
    backToPath={backToPath}
    backToTitle={fToTitle(base)}
    searchTypes={searchTypes}
    searchData={searchData}
    setSearchData={setSearchData}
    handleSearch={handleSearch}
    isSearchDisabled={!searchData.searchValue || !isSearchDirty || !searchData.selectedSearchTypes.length}
    loading={loading}
    handlePaginationChange={handlePaginationChange}
    searchDataMap={searchDataMap}
    searchResults={searchResults}
    filters={filters}
    setFilters={setFilters}
  />
}